import LodgeAttendancePage from './pages/LodgeAttendancePage';
import React, {useEffect, useState} from 'react';
import {BrowserRouter as Router, Route, Routes, useLocation} from 'react-router-dom';
import Modal from 'react-modal';
import axios from 'axios';
import HomePage from './pages/HomePage';
import AdminPage from './pages/AdminPanel';
import DocumentManagement from './pages/DocumentManagement';
import ViewDocuments from './pages/DocumentViewer';
import ProtectedRoute, { AuthReadyContext } from './pages/ProtectedRoute';
import AccountSecurity, { PasskeyPrompt } from './pages/AccountSecurity';
import LoginRegisterPage from './pages/LoginRegisterPage';
import Footer from './pages/Footer';
import Disclaimer from './pages/Disclaimer';
import Layout from './components/Layout';

import CalendarPage from './pages/CalendarPage';
import ResearchPage from './pages/ResearchPage';
import ResourcesPage from './pages/ResourcesPage';
import ResearchPaperBuilder from './pages/ResearchPaperBuilder';
import LostJewelGamePage from './pages/LostJewelGamePage';
import GamesPage from './pages/GamesPage';
import LodgeGamePage from './pages/LodgeGamePage';
import AboutPage from './pages/AboutPage';
import InstructionsPage from './pages/InstructionsPage';
import SurveyPage from './pages/SurveyPage';
import AnalyticsPage from './pages/AnalyticsPage'; // <-- NEW Analytics import
import '@fortawesome/fontawesome-free/css/all.min.css';
import StagingImportsPage from './pages/StagingImportsPage';

Modal.setAppElement('#root');

function getAnalyticsId(storage, key) {
    try {
        const existing = storage.getItem(key);
        if (existing) return existing;
        const generated = window.crypto?.randomUUID?.()
            || 'xxxx-xxxx-xxxx-xxxx'.replace(/[x]/g, () => Math.floor(Math.random() * 16).toString(16));
        storage.setItem(key, generated);
        return generated;
    } catch {
        return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
}

function SiteAnalyticsTracker({user}) {
    const location = useLocation();

    useEffect(() => {
        const requestPath = `${location.pathname}${location.search || ''}`;
        axios.post('/api/public/site-analytics/event', {
            interaction_type: 'site_page_view',
            request_path: requestPath,
            session_id: getAnalyticsId(window.sessionStorage, 'site_session_id'),
            visitor_id: getAnalyticsId(window.localStorage, 'site_visitor_id'),
            user_id: user?.id || user?.userId || null,
            additional_data: {
                page: location.pathname,
            },
        }).catch((error) => {
            console.warn('Could not log site analytics:', error.message);
        });
    }, [location.pathname, location.search, user?.id, user?.userId]);

    return null;
}

function LoginRedirect({ open }) {
    const location = useLocation();
    useEffect(() => { if (location.state?.returnTo) open(); }, [location.state?.returnTo]);
    return null;
}
function App() {
    const [user, setUser] = useState(null);
    const [authReady, setAuthReady] = useState(false);
    const [promptOnLogin, setPromptOnLogin] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await axios.get('/api/current_user');
                const userData = response.data;
                setUser(userData);
                localStorage.setItem('user', JSON.stringify(userData));
            } catch (error) {
                if (error.response?.status === 401) {
                    setUser(null);
                    localStorage.removeItem('user');
                    // Offer sign-in on arrival, after confirming there is no remembered session.
                    // Shared attendance and other direct links keep their own entry flow.
                    if (window.location.pathname === '/') setIsLoginModalOpen(true);
                } else {
                    console.error('Error fetching user:', error);
                }
            }
            finally { setAuthReady(true); }
        };
        fetchUser();
    }, []);

    const openLoginModal = () => setIsLoginModalOpen(true);
    const closeLoginModal = () => setIsLoginModalOpen(false);

    const handleLogout = async () => {
        try {
            await axios.post('/api/logout');
            setUser(null);
            localStorage.removeItem('user');
        } catch (error) {
            console.error('Error during logout:', error);
        }
    };

    return (
<AuthReadyContext.Provider value={authReady}><Router>
            <LoginRedirect open={openLoginModal}/>
            <PasskeyPrompt user={user} eligible={promptOnLogin}/>
            <Disclaimer/>
            <SiteAnalyticsTracker user={user}/>
            <div className="App">
                <Routes>
                    <Route path="/security" element={<Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}><ProtectedRoute user={user}><AccountSecurity/></ProtectedRoute></Layout>}/>

                    <Route
                        path="/"
                        element={
                            <Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}>
                                <HomePage user={user} openLoginModal={openLoginModal}/>
                            </Layout>
                        }
                    />

                    <Route
                        path="/research"
                        element={
                            <Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}>
                                <ResearchPage user={user} openLoginModal={openLoginModal}/>
                            </Layout>
                        }
                    />

                    <Route path="/attendance" element={<Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}><LodgeAttendancePage /></Layout>} />
                    <Route path="/attendance/:lodgeId" element={<Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}><LodgeAttendancePage /></Layout>} />

                    <Route
                        path="/surveys"
                        element={
                            <Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}>
                                <SurveyPage user={user} openLoginModal={openLoginModal}/>
                            </Layout>
                        }
                    />

                    <Route
                        path="/instructions"
                        element={
                            <Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}>
                                <ProtectedRoute user={user} allowedRoles={["Admin", "Archivist", "Member", "Researcher", "Secretary", "Almoner"]}>
                                    <InstructionsPage/>
                                </ProtectedRoute>
                            </Layout>
                        }
                    />

                    <Route
                        path="/resources"
                        element={
                            <Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}>
                                <ProtectedRoute user={user} allowedRoles={["Admin", "Archivist", "Member", "Researcher","Secretary"]}>
                                    <ResourcesPage user={user} openLoginModal={openLoginModal}/>
                                </ProtectedRoute>
                            </Layout>
                        }
                    />

                    <Route
                        path="/research-papers"
                        element={
                            <Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}>
                                <ProtectedRoute user={user} allowedRoles={["Admin", "Archivist", "Member", "Researcher","Secretary"]}>
                                    <ResearchPaperBuilder user={user}/>
                                </ProtectedRoute>
                            </Layout>
                        }
                    />

                    <Route path="/games" element={
                        <Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}>
                            <ProtectedRoute user={user} allowedRoles={["Admin", "Archivist", "Member", "Researcher", "Secretary", "Almoner"]}>
                                <GamesPage/>
                            </ProtectedRoute>
                        </Layout>
                    }/>

                    <Route path="/games/the-lodge" element={
                        <Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}>
                            <ProtectedRoute user={user} allowedRoles={["Admin", "Archivist", "Member", "Researcher", "Secretary", "Almoner"]}>
                                <LodgeGamePage/>
                            </ProtectedRoute>
                        </Layout>
                    }/>

                    <Route
                        path="/lost-jewel"
                        element={
                            <Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}>
                                <ProtectedRoute user={user} allowedRoles={["Admin", "Archivist", "Member", "Researcher", "Secretary", "Almoner"]}>
                                    <LostJewelGamePage user={user}/>
                                </ProtectedRoute>
                            </Layout>
                        }
                    />

                    <Route
                        path="/calendar"
                        element={
                            <Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}>
                                <ProtectedRoute user={user} allowedRoles={["Admin", "Archivist", "Member", "Researcher","Secretary"]}>
                                    <CalendarPage user={user} openLoginModal={openLoginModal}/>
                                </ProtectedRoute>
                            </Layout>
                        }
                    />

                    <Route
                        path="/about"
                        element={
                            <Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}>
                                <AboutPage/>
                            </Layout>
                        }
                    />

                    <Route
                        path="/About"
                        element={
                            <Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}>
                                <AboutPage/>
                            </Layout>
                        }
                    />

                    <Route
                        path="/upload-documents"
                        element={
                            <Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}>
                                <ProtectedRoute user={user} allowedRoles={['Admin', 'Archivist',"Secretary"]}>
                                    <DocumentManagement user={user}/>
                                </ProtectedRoute>
                            </Layout>
                        }
                    />

                    <Route
                        path="/view-documents"
                        element={
                            <Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}>
                                <ProtectedRoute user={user} allowedRoles={["Admin", "Archivist", "Member", "Researcher","Secretary"]}>
                                    <ViewDocuments user={user}/>
                                </ProtectedRoute>
                            </Layout>
                        }
                    />
                    <Route
                        path="/staging-imports"
                        element={
                            <Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}>
                                <ProtectedRoute user={user} allowedRoles={['Admin', 'Archivist']}>
                                    <StagingImportsPage user={user}/>
                                </ProtectedRoute>
                            </Layout>
                        }
                    />
                    <Route
                        path="/admin"
                        element={
                            <Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}>
                                <ProtectedRoute user={user} allowedRoles={['Admin', 'Archivist']}>
                                    <AdminPage user={user}/>
                                </ProtectedRoute>
                            </Layout>
                        }
                    />

                    {/* NEW: Analytics Route */}
                    <Route
                        path="/analytics"
                        element={
                            <Layout user={user} onLogout={handleLogout} openLoginModal={openLoginModal}>
                                <ProtectedRoute user={user} allowedRoles={['Admin', 'Archivist']}>
                                    <AnalyticsPage/>
                                </ProtectedRoute>
                            </Layout>
                        }
                    />
                </Routes>

                {/* Login/Register Modal */}
                <LoginRegisterPage
                    isOpen={isLoginModalOpen}
                    closeModal={closeLoginModal}
                    setUser={(newUser, meta) => {
                        setPromptOnLogin(meta?.password === true);
                        setUser(newUser);
                        localStorage.setItem('user', JSON.stringify(newUser));
                    }}
                />
            </div>
            <Footer/>
        </Router></AuthReadyContext.Provider>
    );
}

export default App;
